'use client';
import React, { useState, useEffect, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

// ─── Mock Data ────────────────────────────────────────────────────────────────
// Backend integration point: GET /api/analytics/qr-performance

const qrRecords = [
  { id: 'qr-001', type: 'url',      content: 'https://knowourindia.com',                   createdAt: '2026-08-03T10:22:00Z', scanCount: 142, downloadCount: 7  },
  { id: 'qr-002', type: 'upi',      content: 'upi://pay?pa=novamax@okaxis',                 createdAt: '2026-08-03T09:15:00Z', scanCount: 58,  downloadCount: 3  },
  { id: 'qr-003', type: 'whatsapp', content: 'https://wa.me/919876543210',                  createdAt: '2026-08-02T16:45:00Z', scanCount: 211, downloadCount: 12 },
  { id: 'qr-004', type: 'wifi',     content: 'WIFI:T:WPA;S:NovaMax_Office_5G',              createdAt: '2026-08-02T14:30:00Z', scanCount: 34,  downloadCount: 2  },
  { id: 'qr-005', type: 'email',    content: 'mailto:hello@knowourindia.com',               createdAt: '2026-08-01T11:20:00Z', scanCount: 76,  downloadCount: 5  },
  { id: 'qr-006', type: 'url',      content: 'https://novamaxstudios.com/portfolio',        createdAt: '2026-08-01T09:05:00Z', scanCount: 389, downloadCount: 18 },
  { id: 'qr-007', type: 'maps',     content: 'https://maps.google.com/?q=India+Gate',       createdAt: '2026-07-31T15:00:00Z', scanCount: 63,  downloadCount: 4  },
  { id: 'qr-008', type: 'phone',    content: 'tel:+919876543210',                           createdAt: '2026-07-31T12:40:00Z', scanCount: 19,  downloadCount: 1  },
  { id: 'qr-009', type: 'text',     content: 'Certificate of Completion — NovaMax Studios', createdAt: '2026-07-30T10:00:00Z', scanCount: 504, downloadCount: 23 },
  { id: 'qr-010', type: 'url',      content: 'https://knowourindia.com/states/rajasthan',   createdAt: '2026-07-29T08:30:00Z', scanCount: 178, downloadCount: 9  },
];

const downloadTrend = [
  { date: 'Jul 25', downloads: 12, scans: 48  },
  { date: 'Jul 26', downloads: 18, scans: 72  },
  { date: 'Jul 27', downloads: 9,  scans: 36  },
  { date: 'Jul 28', downloads: 24, scans: 96  },
  { date: 'Jul 29', downloads: 31, scans: 124 },
  { date: 'Jul 30', downloads: 27, scans: 108 },
  { date: 'Jul 31', downloads: 19, scans: 76  },
  { date: 'Aug 01', downloads: 38, scans: 152 },
  { date: 'Aug 02', downloads: 44, scans: 176 },
  { date: 'Aug 03', downloads: 52, scans: 208 },
];

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  url:      { label: 'URL',       color: '#2563EB', bg: '#EFF6FF' },
  upi:      { label: 'UPI',       color: '#7C3AED', bg: '#F5F3FF' },
  whatsapp: { label: 'WhatsApp',  color: '#16A34A', bg: '#F0FDF4' },
  wifi:     { label: 'WiFi',      color: '#D97706', bg: '#FFFBEB' },
  email:    { label: 'Email',     color: '#9333EA', bg: '#FAF5FF' },
  maps:     { label: 'Maps',      color: '#DC2626', bg: '#FFF1F2' },
  phone:    { label: 'Phone',     color: '#0891B2', bg: '#ECFEFF' },
  text:     { label: 'Text',      color: '#475569', bg: '#F8FAFC' },
};

const PIE_COLORS = ['#2563EB', '#7C3AED', '#16A34A', '#D97706', '#9333EA', '#DC2626', '#0891B2', '#475569'];

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon, accent }: {
  label: string; value: string | number; sub?: string; icon: React.ReactNode; accent: string;
}) {
  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-5 flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: accent }}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-muted-foreground font-medium truncate" style={{ fontSize: '12px' }}>{label}</p>
        <p className="font-extrabold text-foreground mt-0.5" style={{ fontSize: '24px', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</p>
        {sub && <p className="text-muted-foreground mt-1" style={{ fontSize: '12px' }}>{sub}</p>}
      </div>
    </div>
  );
}

interface QRRecord {
  id: string; type: string; content: string; createdAt: string; scanCount: number; downloadCount: number;
}

function TopPerformerRow({ record, rank }: { record: QRRecord; rank: number }) {
  const meta = TYPE_META[record.type] ?? { label: record.type, color: '#475569', bg: '#F8FAFC' };
  const shortContent = record.content.length > 42 ? record.content.slice(0, 42) + '…' : record.content;
  const date = new Date(record.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="flex items-center gap-4 px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <span className="font-bold text-muted-foreground w-5 text-center flex-shrink-0" style={{ fontSize: '13px' }}>
        {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
      </span>
      <span
        className="px-2.5 py-1 rounded-full font-semibold flex-shrink-0"
        style={{ fontSize: '11px', background: meta.bg, color: meta.color }}
      >
        {meta.label}
      </span>
      <span className="flex-1 text-foreground font-medium truncate" style={{ fontSize: '13px' }}>{shortContent}</span>
      <span className="text-muted-foreground flex-shrink-0" style={{ fontSize: '12px' }}>{date}</span>
      <div className="flex items-center gap-1 flex-shrink-0">
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round">
          <path d="M6.5 1.5v7M6.5 8.5L4 6M6.5 8.5L9 6" /><path d="M1.5 11h10" />
        </svg>
        <span className="font-semibold text-foreground" style={{ fontSize: '13px' }}>{record.downloadCount}</span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#7C3AED" strokeWidth="1.6" strokeLinecap="round">
          <circle cx="6.5" cy="6.5" r="5.5" /><path d="M4.5 6.5h4M6.5 4.5v4" />
        </svg>
        <span className="font-semibold text-foreground" style={{ fontSize: '13px' }}>{record.scanCount}</span>
      </div>
    </div>
  );
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-2xl shadow-lg px-4 py-3 min-w-[130px]">
      <p className="text-muted-foreground mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: p.color, fontWeight: 600 }}>
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-bold text-foreground" style={{ fontSize: '13px' }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function QRAnalyticsClient() {
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'scanCount' | 'downloadCount' | 'createdAt'>('scanCount');

  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  const totalScans = useMemo(() => qrRecords.reduce((s, r) => s + r.scanCount, 0), []);
  const totalDownloads = useMemo(() => qrRecords.reduce((s, r) => s + r.downloadCount, 0), []);
  const topRecord = useMemo(() => [...qrRecords].sort((a, b) => b.scanCount - a.scanCount)[0], []);

  const typeDistribution = useMemo(() => {
    const map: Record<string, number> = {};
    qrRecords.forEach(r => { map[r.type] = (map[r.type] ?? 0) + 1; });
    return Object.entries(map)
      .map(([type, count]) => ({ name: TYPE_META[type]?.label ?? type, value: count }))
      .sort((a, b) => b.value - a.value);
  }, []);

  const typeBarData = useMemo(() => {
    const map: Record<string, { scans: number; downloads: number }> = {};
    qrRecords.forEach(r => {
      if (!map[r.type]) map[r.type] = { scans: 0, downloads: 0 };
      map[r.type].scans += r.scanCount;
      map[r.type].downloads += r.downloadCount;
    });
    return Object.entries(map)
      .map(([type, v]) => ({ type: TYPE_META[type]?.label ?? type, ...v }))
      .sort((a, b) => b.scans - a.scans);
  }, []);

  const sortedRecords = useMemo(() =>
    [...qrRecords].sort((a, b) => {
      if (sortBy === 'createdAt') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return b[sortBy] - a[sortBy];
    }), [sortBy]);

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="px-6 lg:px-8 xl:px-10 pt-8 pb-6 border-b border-border bg-card">
          <div className="max-w-screen-2xl mx-auto">
            <div className="animate-pulse bg-muted rounded-2xl h-8 w-48 mb-2" />
            <div className="animate-pulse bg-muted rounded h-4 w-72" />
          </div>
        </div>
        <div className="px-6 lg:px-8 xl:px-10 py-6">
          <div className="max-w-screen-2xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={`sk-${i}`} className="bg-card rounded-3xl border border-border shadow-card p-5 h-24 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* ── Page Header ── */}
      <div className="px-6 lg:px-8 xl:px-10 pt-8 pb-6 border-b border-border bg-card">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-lg gradient-primary flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
                <path d="M1 9L4 6L7 8L11 3" /><circle cx="11" cy="3" r="1" fill="white" stroke="none" />
              </svg>
            </div>
            <span className="badge badge-blue">Performance</span>
          </div>
          <h1 className="font-extrabold text-foreground" style={{ fontSize: '26px', letterSpacing: '-0.02em' }}>QR Analytics</h1>
          <p className="text-muted-foreground mt-0.5" style={{ fontSize: '14px' }}>
            Individual QR code performance — scan counts, generation dates, popular types &amp; download trends.
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-8 xl:px-10 py-6">
        <div className="max-w-screen-2xl mx-auto space-y-6">

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Scans"
              value={totalScans.toLocaleString()}
              sub="Across all QR codes"
              accent="#EFF6FF"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#2563EB" strokeWidth="1.7" strokeLinecap="round"><circle cx="9" cy="9" r="3.5" /><path d="M9 2v2M9 14v2M2 9h2M14 9h2M4.2 4.2l1.4 1.4M12.4 12.4l1.4 1.4M4.2 13.8l1.4-1.4M12.4 5.6l1.4-1.4" /></svg>}
            />
            <StatCard
              label="Total Downloads"
              value={totalDownloads.toLocaleString()}
              sub="PNG · SVG · PDF"
              accent="#F5F3FF"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#7C3AED" strokeWidth="1.7" strokeLinecap="round"><path d="M9 11V3M9 11L6 8M9 11L12 8" /><path d="M2 15h14" /></svg>}
            />
            <StatCard
              label="QR Codes Tracked"
              value={qrRecords.length}
              sub={`${typeDistribution.length} types used`}
              accent="#F0FDF4"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round"><rect x="2" y="2" width="5" height="5" rx="1" /><rect x="11" y="2" width="5" height="5" rx="1" /><rect x="2" y="11" width="5" height="5" rx="1" /><path d="M11 11h2M15 11h.5M11 13.5h1.5M13.5 13.5H15M11 16h4" /></svg>}
            />
            <StatCard
              label="Top Performer"
              value={topRecord.scanCount}
              sub={`${TYPE_META[topRecord.type]?.label ?? topRecord.type} · most scanned`}
              accent="#FFFBEB"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#D97706" strokeWidth="1.7" strokeLinecap="round"><path d="M9 2l1.8 3.6 4 .6-2.9 2.8.7 4L9 11l-3.6 1.9.7-4L3.2 6.2l4-.6L9 2Z" /></svg>}
            />
          </div>

          {/* ── Download & Scan Trend ── */}
          <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Download &amp; Scan Trends</h2>
                <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Last 10 days activity</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: '#2563EB', fontWeight: 600 }}>
                  <span className="w-3 h-1.5 rounded-full inline-block" style={{ background: '#2563EB' }} /> Downloads
                </span>
                <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: '#7C3AED', fontWeight: 600 }}>
                  <span className="w-3 h-1.5 rounded-full inline-block" style={{ background: '#7C3AED' }} /> Scans
                </span>
              </div>
            </div>
            <div className="px-4 py-5" style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={downloadTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradDownloads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradScans" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.12} />
                      <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="downloads" name="Downloads" stroke="#2563EB" strokeWidth={2} fill="url(#gradDownloads)" dot={false} activeDot={{ r: 4, fill: '#2563EB' }} />
                  <Area type="monotone" dataKey="scans" name="Scans" stroke="#7C3AED" strokeWidth={2} fill="url(#gradScans)" dot={false} activeDot={{ r: 4, fill: '#7C3AED' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Type Distribution + Bar Chart ── */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* Pie — Most Popular Types */}
            <div className="xl:col-span-2 bg-card rounded-3xl border border-border shadow-card overflow-hidden">
              <div className="px-6 py-5 border-b border-border">
                <h2 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Most Popular QR Types</h2>
                <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>By number of QR codes generated</p>
              </div>
              <div className="px-4 py-4" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={typeDistribution}
                      cx="50%"
                      cy="45%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {typeDistribution.map((_, idx) => (
                        <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [`${v} QR codes`, '']} contentStyle={{ borderRadius: '12px', border: '1px solid var(--border)', fontSize: '12px' }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Bar — Scans by Type */}
            <div className="xl:col-span-3 bg-card rounded-3xl border border-border shadow-card overflow-hidden">
              <div className="px-6 py-5 border-b border-border">
                <h2 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Scans by QR Type</h2>
                <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Total scan count per content type</p>
              </div>
              <div className="px-4 py-4" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={typeBarData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={22}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="type" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="scans" name="Scans" fill="#2563EB" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="downloads" name="Downloads" fill="#7C3AED" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ── Top Performers Table ── */}
          <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between gap-4">
              <div>
                <h2 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Individual QR Performance</h2>
                <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Scan count, generation date &amp; download count per QR code</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground" style={{ fontSize: '12px' }}>Sort by</span>
                {(['scanCount', 'downloadCount', 'createdAt'] as const).map(opt => (
                  <button
                    key={opt}
                    onClick={() => setSortBy(opt)}
                    className={`px-3 py-1.5 rounded-xl font-semibold transition-all ${sortBy === opt ? 'gradient-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                    style={{ fontSize: '12px' }}
                  >
                    {opt === 'scanCount' ? 'Scans' : opt === 'downloadCount' ? 'Downloads' : 'Date'}
                  </button>
                ))}
              </div>
            </div>
            {/* Table header */}
            <div className="flex items-center gap-4 px-5 py-2.5 bg-muted/40 border-b border-border">
              <span className="w-5 flex-shrink-0" />
              <span className="w-20 flex-shrink-0 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Type</span>
              <span className="flex-1 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Content</span>
              <span className="flex-shrink-0 text-muted-foreground font-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Generated</span>
              <span className="flex-shrink-0 text-muted-foreground font-semibold w-16 text-right" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>DL</span>
              <span className="flex-shrink-0 text-muted-foreground font-semibold w-16 text-right" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Scans</span>
            </div>
            {sortedRecords.map((r, i) => (
              <TopPerformerRow key={r.id} record={r} rank={i + 1} />
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
